import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'

export default function ReportAtleta({ atleta, gruppoId, onChiudi }) {
  const [sessioni, setSessioni] = useState([])
  const [serie, setSerie] = useState([])
  const [tracking, setTracking] = useState([])
  const [periodo, setPeriodo] = useState(30)
  const [caricamento, setCaricamento] = useState(true)
  const [exAperto, setExAperto] = useState(null)        // nome esercizio con grafico aperto
  const [datiGrafico, setDatiGrafico] = useState({})    // { nomeEx: [...] }

  useEffect(() => { caricaDati() }, [periodo])

  async function caricaDati() {
    setCaricamento(true)
    const dataInizio = new Date(Date.now() - periodo * 24 * 60 * 60 * 1000).toISOString().split('T')[0]

    const { data: sessioniData } = await supabase
      .from('risultati_sessione')
      .select('*, sessioni_calendario(data, sessioni_tipo(nome))')
      .eq('atleta_id', atleta.atleta_id)
      .gte('creato_il', dataInizio)
      .order('creato_il', { ascending: false })

    const sessioniList = sessioniData || []
    setSessioni(sessioniList)

    const sessioniIds = sessioniList.map(s => s.id)

    const [{ data: serieData }, { data: trackingData }] = await Promise.all([
      sessioniIds.length > 0
        ? supabase.from('risultati_serie')
            .select('*')
            .in('risultato_sessione_id', sessioniIds)
            .order('creato_il', { ascending: true })
        : Promise.resolve({ data: [] }),
      (() => {
        let q = supabase.from('tracking_esercizio')
          .select('*, esercizi_db(nome, tipo_misura)')
          .eq('atleta_id', atleta.atleta_id)
          .gte('data', dataInizio)
          .order('data', { ascending: true })
        // Se vista coach: mostra solo esercizi assegnati dal gruppo, non quelli personali
        if (gruppoId) q = q.neq('fonte', 'personale')
        return q
      })(),
    ])

    setSerie(serieData || [])
    setTracking(trackingData || [])
    setCaricamento(false)
  }

  async function apriGrafico(nomeEx, esId) {
    if (exAperto === nomeEx) { setExAperto(null); return }
    setExAperto(nomeEx)
    if (datiGrafico[nomeEx]) return  // già caricato

    // Carica tutto il tracking per questo esercizio nel periodo
    const dataInizio = new Date(Date.now() - periodo * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    let query = supabase.from('tracking_esercizio')
      .select('data, ripetizioni, durata_secondi, peso, elastico, fonte')
      .eq('atleta_id', atleta.atleta_id)
      .gte('data', dataInizio)
      .order('data', { ascending: true })
    // Se vista coach: escludi esercizi personali
    if (gruppoId) query = query.neq('fonte', 'personale')

    if (esId) query = query.eq('esercizio_id', esId)
    else query = query  // fallback: usa le serie

    const { data } = await query
    const punti = (data || []).map(t => ({
      data: new Date(t.data).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }), // "21/09": "21 set" si leggeva come "21 serie"
      rip: t.ripetizioni || t.durata_secondi || null,
      kg: t.elastico ? null : (t.peso || 0),
      elastico: t.elastico || null,
      fonte: t.fonte,
    }))
    setDatiGrafico(prev => ({ ...prev, [nomeEx]: punti }))
  }

  const completate = sessioni.filter(s => s.completata).length
  const aderenza = sessioni.length > 0 ? Math.round((completate / sessioni.length) * 100) : 0

  // Raggruppa serie per esercizio con statistiche
  const perEsercizio = {}
  serie.forEach(s => {
    if (!s.esercizio_nome) return
    if (!perEsercizio[s.esercizio_nome]) perEsercizio[s.esercizio_nome] = { serie: [], esId: s.esercizio_id }
    perEsercizio[s.esercizio_nome].serie.push(s)
  })

  // Arricchisce con dati dal tracking (include allenamenti personali)
  const eserciziFatti = {}
  tracking.forEach(t => {
    const nome = t.esercizi_db?.nome || 'Esercizio'
    if (!eserciziFatti[nome]) eserciziFatti[nome] = { records: [], esId: t.esercizio_id, tipoMisura: t.esercizi_db?.tipo_misura }
    eserciziFatti[nome].records.push(t)
  })

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }} className="fade-in">
      {/* Intestazione su due righe: prima freccia, badge, nome e filtri stavano tutti
          in una sola riga, e il nome dell'atleta andava a capo spezzato. */}
      <div className="topbar">
        <button className="back-btn" onClick={onChiudi} aria-label="Indietro">←</button>
        <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 16, flex: 1, minWidth: 0, textAlign: 'center',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', margin: '0 8px' }}>
          {atleta.nome} {atleta.cognome}
        </div>
        {/* Spazio pari alla freccia, così il nome resta davvero centrato */}
        <div style={{ width: 32, flexShrink: 0 }} />
      </div>
      {/* Selettore del periodo a tutta larghezza, con le parole intere: "7g/30g/90g"
          erano sigle da decifrare e pulsanti piccoli da colpire col dito. */}
      <div style={{ padding: '10px 20px', borderBottom: '1px solid var(--border)' }}>
        <div role="group" aria-label="Periodo del report"
          style={{ display: 'flex', background: 'var(--surface2)', borderRadius: 10, padding: 3, gap: 3 }}>
          {[7, 30, 90].map(g => (
            <button key={g} onClick={() => { setPeriodo(g); setExAperto(null); setDatiGrafico({}) }}
              aria-pressed={periodo === g}
              style={{ flex: 1, padding: '9px 4px', borderRadius: 8, border: 'none', cursor: 'pointer',
                background: periodo === g ? 'var(--surface)' : 'transparent',
                color: periodo === g ? 'var(--accent)' : 'var(--muted)',
                boxShadow: periodo === g ? '0 1px 3px rgba(0,0,0,0.25)' : 'none',
                fontFamily: 'Syne', fontSize: 13, fontWeight: 700, transition: 'all 0.15s' }}>
              {g} giorni
            </button>
          ))}
        </div>
      </div>

      <div className="scroll-area">
        {gruppoId && (
          <div style={{ fontSize: 12, color: 'var(--accent)', background: 'rgba(127,214,61,0.08)', border: '1px solid rgba(127,214,61,0.2)', borderRadius: 10, padding: '8px 12px' }}>
            👁 Vista coach: vedi solo gli esercizi svolti nel gruppo. Gli allenamenti personali dell'atleta restano privati.
          </div>
        )}
        {caricamento ? (
          <div style={{ textAlign: 'center', color: 'var(--muted)', padding: 40 }}>Caricamento report...</div>
        ) : <>

          {/* Stat box */}
          <div className="stat-grid">
            <div className="stat-box">
              <div className="stat-val" style={{ color: 'var(--accent)' }}>{sessioni.length}</div>
              <div className="stat-label">Sessioni totali</div>
            </div>
            <div className="stat-box">
              <div className="stat-val" style={{ color: 'var(--green)' }}>{completate}</div>
              <div className="stat-label">Completate</div>
            </div>
            <div className="stat-box">
              {/* Senza sessioni programmate l'aderenza non esiste: prima compariva "0%" in rosso,
                  come se l'atleta le avesse saltate tutte, anche quando il coach non ne aveva assegnate */}
              {sessioni.length === 0 ? (
                <>
                  <div className="stat-val" style={{ color: 'var(--muted)' }}>—</div>
                  <div className="stat-label">Aderenza · nessuna sessione</div>
                </>
              ) : (
                <>
                  <div className="stat-val" style={{ color: aderenza >= 70 ? 'var(--green)' : aderenza >= 40 ? 'var(--accent)' : 'var(--red)' }}>{aderenza}%</div>
                  <div className="stat-label">Aderenza</div>
                </>
              )}
            </div>
            <div className="stat-box">
              <div className="stat-val" style={{ color: 'var(--accent2)' }}>{serie.length}</div>
              <div className="stat-label">Serie totali</div>
            </div>
          </div>

          {/* Grafico aderenza */}
          <div className="card">
            <div className="card-header"><div className="card-title">📊 Aderenza ultimi {periodo} giorni</div></div>
            <div className="card-body">
              {/* Al massimo 30 colonne: oltre i 30 giorni ogni colonna copre più giorni.
                  Prima il grafico si fermava a 30 colonne da 1 giorno, quindi a "90 giorni"
                  mostrava solo l'ultimo mese e tutto il resto era invisibile. */}
              {(() => { const gpc = Math.ceil(periodo / 30); return gpc > 1 ? (
                <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 6 }}>Ogni colonna = {gpc} giorni</div>
              ) : null })()}
              <div style={{ display: 'flex', gap: 3, alignItems: 'flex-end', height: 60 }}>
                {(() => {
                  // Numero di colonne calcolato qui: Array.from passa alla funzione solo
                  // (valore, indice), NON l'array — leggere arr.length mandava in crash il report.
                  const colonne = Math.ceil(periodo / Math.ceil(periodo / 30))
                  return Array.from({ length: colonne }, (_, i) => {
                  const gpc = Math.ceil(periodo / 30)            // giorni per colonna: 1 fino a 30 giorni, 3 a 90
                  const giorniFa = (colonne - 1 - i) * gpc   // la colonna copre [giorniFa, giorniFa + gpc)
                  const giorni = Array.from({ length: gpc }, (_, k) =>
                    new Date(Date.now() - (giorniFa + k) * 24 * 60 * 60 * 1000).toISOString().split('T')[0])
                  const nella = sessioni.filter(s => giorni.includes(s.sessioni_calendario?.data))
                  const hasSessione = nella.length > 0
                  const completata = hasSessione && nella.every(s => s.completata)
                  return (
                    <div key={i} style={{ flex: 1, height: hasSessione ? (completata ? '100%' : '50%') : '15%',
                      borderRadius: '3px 3px 0 0',
                      background: completata ? 'var(--accent)' : hasSessione ? 'var(--accent3)' : 'rgba(255,255,255,0.06)',
                      transition: 'height 0.5s ease' }} />
                  )
                  })
                })()}
              </div>
              <div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: 10, color: 'var(--muted)' }}>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}><div style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--accent)' }} /><span>Completata</span></div>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}><div style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--accent3)' }} /><span>Non completata</span></div>
              </div>
            </div>
          </div>

          {/* Progressi per esercizio — con grafico espandibile */}
          {Object.keys(eserciziFatti).length > 0 && (
            <div className="card">
              <div className="card-header">
                <div className="card-title">📈 Progressi per esercizio</div>
                <span style={{ fontSize: 11, color: 'var(--muted)' }}>Tocca per il grafico</span>
              </div>
              <div style={{ padding: '0 16px' }}>
                {Object.entries(eserciziFatti).map(([nome, { records, esId, tipoMisura }], i) => {
                  const maxRip = Math.max(0, ...records.filter(r => r.ripetizioni).map(r => r.ripetizioni))
                  const maxTempo = Math.max(0, ...records.filter(r => r.durata_secondi).map(r => r.durata_secondi))
                  const maxPeso = Math.max(0, ...records.filter(r => r.peso > 0).map(r => r.peso))
                  const daGruppo = records.filter(r => r.fonte !== 'personale').length
                  const daPersonale = records.filter(r => r.fonte === 'personale').length
                  const isAperto = exAperto === nome
                  const grafPunti = datiGrafico[nome] || []
                  const isUltimo = i === Object.keys(eserciziFatti).length - 1

                  return (
                    <div key={nome} style={{ borderBottom: !isUltimo ? '1px solid var(--border)' : 'none' }}>
                      {/* Riga esercizio cliccabile */}
                      <div onClick={() => apriGrafico(nome, esId)}
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '12px 0', cursor: 'pointer' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{nome}</div>
                          <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                            {maxRip > 0 && (
                              <span style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 700 }}>
                                {maxRip} rip max
                              </span>
                            )}
                            {maxTempo > 0 && (
                              <span style={{ fontSize: 11, color: 'var(--accent2)', fontWeight: 700 }}>
                                {maxTempo}s max
                              </span>
                            )}
                            {maxPeso > 0 && (
                              <span style={{ fontSize: 11, color: 'var(--accent3)', fontWeight: 700 }}>
                                +{maxPeso}kg max
                              </span>
                            )}
                            {/* Giorni distinti, non righe (ogni serie è una riga). Detto "volte" e non "sessioni":
                                include anche giorni di sessioni poi eliminate dal calendario, che il
                                riepilogo in alto non conta più — con la stessa parola i due numeri si contraddicevano. */}
                            {(() => { const n = new Set(records.map(r => r.data)).size; return (
                              <span style={{ fontSize: 10, color: 'var(--muted)' }}>fatto {n} {n === 1 ? 'volta' : 'volte'}</span>
                            ) })()}
                            {daPersonale > 0 && (
                              <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 8, fontWeight: 700,
                                background: 'rgba(87,184,255,0.12)', color: 'var(--accent2)' }}>
                                +{daPersonale} personali
                              </span>
                            )}
                          </div>
                        </div>
                        <span style={{ color: 'var(--muted)', fontSize: 14, transition: 'transform 0.2s',
                          transform: isAperto ? 'rotate(90deg)' : 'none', flexShrink: 0, marginLeft: 8 }}>›</span>
                      </div>

                      {/* Grafico espanso */}
                      {isAperto && (
                        <div style={{ paddingBottom: 16 }} className="fade-in">
                          {grafPunti.length === 0 ? (
                            <div style={{ textAlign: 'center', color: 'var(--muted)', fontSize: 12, padding: '12px 0' }}>
                              Caricamento grafico...
                            </div>
                          ) : grafPunti.length < 2 ? (
                            <div style={{ textAlign: 'center', color: 'var(--muted)', fontSize: 12, padding: '12px 0' }}>
                              Servono almeno 2 sessioni per visualizzare il grafico
                            </div>
                          ) : (
                            <>
                              {/* Grafico rip/tempo */}
                              <div style={{ marginBottom: 12 }}>
                                <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6, fontFamily: 'Syne', fontWeight: 700 }}>
                                  {tipoMisura === 'tempo' ? '⏱️ Secondi nel tempo' : '🔁 Ripetizioni nel tempo'}
                                </div>
                                <ResponsiveContainer width="100%" height={150}>
                                  <LineChart data={grafPunti} margin={{ top: 4, right: 8, left: -24, bottom: 4 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                                    <XAxis dataKey="data" tick={{ fontSize: 9, fill: 'var(--muted)' }} />
                                    <YAxis tick={{ fontSize: 9, fill: 'var(--muted)' }} />
                                    <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 11 }} />
                                    <Line type="monotone" dataKey="rip" stroke="var(--accent)" strokeWidth={2}
                                      dot={{ fill: 'var(--accent)', r: 3 }} name={tipoMisura === 'tempo' ? 'Sec' : 'Rip'} connectNulls />
                                  </LineChart>
                                </ResponsiveContainer>
                              </div>

                              {/* Grafico kg se presente */}
                              {grafPunti.some(p => p.kg > 0) && (
                                <div>
                                  <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6, fontFamily: 'Syne', fontWeight: 700 }}>⚖️ Carico nel tempo (kg)</div>
                                  <ResponsiveContainer width="100%" height={130}>
                                    <LineChart data={grafPunti} margin={{ top: 4, right: 8, left: -24, bottom: 4 }}>
                                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                                      <XAxis dataKey="data" tick={{ fontSize: 9, fill: 'var(--muted)' }} />
                                      <YAxis tick={{ fontSize: 9, fill: 'var(--muted)' }} />
                                      <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 11 }} />
                                      <Line type="monotone" dataKey="kg" stroke="var(--purple)" strokeWidth={2}
                                        dot={{ fill: 'var(--purple)', r: 3 }} name="KG" connectNulls />
                                    </LineChart>
                                  </ResponsiveContainer>
                                </div>
                              )}

                              {/* Ultimi risultati */}
                              <div style={{ marginTop: 10, background: 'var(--surface2)', borderRadius: 10, padding: '8px 12px' }}>
                                <div style={{ fontSize: 10, fontFamily: 'Syne', fontWeight: 700, color: 'var(--muted)', marginBottom: 6, letterSpacing: 0.5, textTransform: 'uppercase' }}>Ultimi risultati</div>
                                {grafPunti.slice(-5).reverse().map((p, j) => (
                                  <div key={j} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', fontSize: 12 }}>
                                    <span style={{ color: 'var(--muted)' }}>{p.data}</span>
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                      {p.rip > 0 && <span style={{ color: 'var(--accent)', fontWeight: 700 }}>{p.rip} {tipoMisura === 'tempo' ? 's' : 'rip'}</span>}
                                      {p.kg > 0 && <span style={{ color: 'var(--accent3)' }}>+{p.kg}kg</span>}
                                      {p.elastico && <span style={{ color: 'var(--accent3)', fontSize: 11 }}>🦺 {p.elastico}</span>}
                                      <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 6, fontWeight: 700,
                                        background: p.fonte === 'personale' ? 'rgba(87,184,255,0.12)' : 'rgba(127,214,61,0.12)',
                                        color: p.fonte === 'personale' ? 'var(--accent2)' : 'var(--accent)' }}>
                                        {p.fonte === 'personale' ? 'Solo' : 'Gruppo'}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Ultime sessioni */}
          <div className="card">
            <div className="card-header"><div className="card-title">📅 Ultime sessioni</div></div>
            <div style={{ padding: '0 16px' }}>
              {sessioni.length === 0 ? (
                <div style={{ padding: '16px 0', textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>Nessuna sessione in questo periodo</div>
              ) : sessioni.slice(0, 10).map((s, i) => (
                <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: i < Math.min(sessioni.length, 10) - 1 ? '1px solid var(--border)' : 'none' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{s.sessioni_calendario?.sessioni_tipo?.nome || 'Sessione'}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                      {s.sessioni_calendario?.data ? new Date(s.sessioni_calendario.data).toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' }) : '—'}
                    </div>
                  </div>
                  <span className={`badge ${s.completata ? 'badge-green' : 'badge-orange'}`}>
                    {s.completata ? '✓ Ok' : 'Parziale'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </>}
      </div>
    </div>
  )
}
